/**
 * Chat Drawer - Slide-in chat interface
 */
import React, { useState, useRef, useEffect } from 'react';
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  Divider,
  List,
  ListItem,
  Paper,
  TextField,
  Button,
  CircularProgress,
  Chip,
  Stack,
  Link
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SendIcon from '@mui/icons-material/Send';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import FiberNewIcon from '@mui/icons-material/FiberNew';
import SmartToyIcon from '@mui/icons-material/SmartToy';

// Enhanced markdown parser component
const MarkdownText = ({ text, isUserMessage = false }) => {
  // Parse inline markdown (bold, italic, links, code)
  const parseInline = (content) => {
    const elements = [];
    let remaining = content;
    let key = 0;

    while (remaining.length > 0) {
      // Check for markdown link: [text](url)
      const linkMatch = remaining.match(/^\[([^\]]+)\]\(([^)]+)\)/);
      if (linkMatch) {
        elements.push(
          <Link
            key={key++}
            href={linkMatch[2]}
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              color: 'inherit',
              textDecoration: 'underline',
              fontWeight: 'bold',
              '&:hover': { opacity: 0.8 }
            }}
          >
            {linkMatch[1]}
          </Link>
        );
        remaining = remaining.slice(linkMatch[0].length);
        continue;
      }

      // Check for bold: **text** or __text__
      const boldMatch = remaining.match(/^\*\*([^*]+)\*\*/) || remaining.match(/^__([^_]+)__/);
      if (boldMatch) {
        elements.push(<strong key={key++}>{boldMatch[1]}</strong>);
        remaining = remaining.slice(boldMatch[0].length);
        continue;
      }

      // Check for italic: *text* or _text_ (but not inside words)
      const italicMatch = remaining.match(/^\*([^*]+)\*/) || remaining.match(/^_([^_]+)_/);
      if (italicMatch) {
        elements.push(<em key={key++}>{italicMatch[1]}</em>);
        remaining = remaining.slice(italicMatch[0].length);
        continue;
      }

      // Check for inline code: `code`
      const codeMatch = remaining.match(/^`([^`]+)`/);
      if (codeMatch) {
        elements.push(
          <code
            key={key++}
            style={{
              backgroundColor: isUserMessage ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.08)',
              padding: '2px 6px',
              borderRadius: '4px',
              fontFamily: 'monospace',
              fontSize: '0.9em'
            }}
          >
            {codeMatch[1]}
          </code>
        );
        remaining = remaining.slice(codeMatch[0].length);
        continue;
      }

      // No match - add next character as text
      const nextSpecial = remaining.search(/[\[*_`]/);
      if (nextSpecial === -1) {
        elements.push(<span key={key++}>{remaining}</span>);
        break;
      } else if (nextSpecial === 0) {
        // Special char didn't match a pattern, treat as literal
        elements.push(<span key={key++}>{remaining[0]}</span>);
        remaining = remaining.slice(1);
      } else {
        elements.push(<span key={key++}>{remaining.slice(0, nextSpecial)}</span>);
        remaining = remaining.slice(nextSpecial);
      }
    }

    return elements;
  };

  // Parse block-level markdown (headers, lists, etc.)
  const parseBlocks = (content) => {
    const lines = content.split('\n');
    const blocks = [];
    let key = 0;

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];

      // Check for numbered list: 1. item
      const numberedMatch = line.match(/^(\d+)\.\s+(.+)$/);
      if (numberedMatch) {
        blocks.push(
          <Box key={key++} sx={{ display: 'flex', gap: 1, ml: 1, my: 0.5 }}>
            <Typography component="span" variant="body2" sx={{ minWidth: '20px' }}>
              {numberedMatch[1]}.
            </Typography>
            <Typography component="span" variant="body2">
              {parseInline(numberedMatch[2])}
            </Typography>
          </Box>
        );
        continue;
      }

      // Check for bullet list: - item or * item
      const bulletMatch = line.match(/^[-*]\s+(.+)$/);
      if (bulletMatch) {
        blocks.push(
          <Box key={key++} sx={{ display: 'flex', gap: 1, ml: 1, my: 0.5 }}>
            <Typography component="span" variant="body2">•</Typography>
            <Typography component="span" variant="body2">
              {parseInline(bulletMatch[1])}
            </Typography>
          </Box>
        );
        continue;
      }

      // Check for emoji bullet (✅, ❌, etc.)
      const emojiBulletMatch = line.match(/^([✅❌🔍📦🏷️🔧📥⚠️💡✨🎯🚀]+)\s+(.+)$/);
      if (emojiBulletMatch) {
        blocks.push(
          <Box key={key++} sx={{ display: 'flex', gap: 1, ml: 1, my: 0.5 }}>
            <Typography component="span" variant="body2">{emojiBulletMatch[1]}</Typography>
            <Typography component="span" variant="body2">
              {parseInline(emojiBulletMatch[2])}
            </Typography>
          </Box>
        );
        continue;
      }

      // Empty line
      if (line.trim() === '') {
        blocks.push(<Box key={key++} sx={{ height: 8 }} />);
        continue;
      }

      // Regular paragraph
      blocks.push(
        <Typography key={key++} component="div" variant="body2" sx={{ my: 0.5 }}>
          {parseInline(line)}
        </Typography>
      );
    }

    return blocks;
  };

  return <Box>{parseBlocks(text)}</Box>;
};

const ChatDrawer = ({
  open,
  onClose,
  messages,
  loading,
  suggestions,
  onSendMessage,
  onClearSession,
  onNewSession
}) => {
  const [inputValue, setInputValue] = useState('');
  const messagesEndRef = useRef(null);

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (inputValue.trim() && !loading) {
      onSendMessage(inputValue.trim());
      setInputValue('');
    }
  };

  const handleSuggestionClick = (suggestion) => {
    if (!loading) {
      onSendMessage(suggestion);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      sx={{
        '& .MuiDrawer-paper': {
          width: { xs: '100%', sm: 400 },
          maxWidth: '100%',
        },
      }}
    >
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        {/* Header */}
        <Box
          sx={{
            p: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderBottom: 1,
            borderColor: 'divider',
            bgcolor: 'primary.main',
            color: 'primary.contrastText'
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <SmartToyIcon />
            <Typography variant="h6" component="h2">
              ClutterLogix Assistant
            </Typography>
          </Box>
          <Box>
            <IconButton
              size="small"
              onClick={onNewSession}
              sx={{ color: 'inherit', mr: 1 }}
              title="New conversation"
            >
              <FiberNewIcon />
            </IconButton>
            <IconButton
              size="small"
              onClick={onClearSession}
              sx={{ color: 'inherit', mr: 1 }}
              title="Clear conversation"
            >
              <DeleteOutlineIcon />
            </IconButton>
            <IconButton
              edge="end"
              onClick={onClose}
              sx={{ color: 'inherit' }}
              aria-label="close"
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>

        {/* Messages Area */}
        <Box
          sx={{
            flex: 1,
            overflowY: 'auto',
            p: 2,
            bgcolor: 'grey.50'
          }}
        >
          {messages.length === 0 ? (
            <Box sx={{ textAlign: 'center', mt: 4 }}>
              <SmartToyIcon sx={{ fontSize: 60, color: 'primary.main', mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                Hi! I'm your ClutterLogix Assistant 👋
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                I can help you organize your inventory, generate reports, and answer questions about the app.
              </Typography>
              
              {/* Suggested Questions */}
              {suggestions.length > 0 && (
                <Stack spacing={1} sx={{ mt: 3 }}>
                  <Typography variant="caption" color="text.secondary">
                    Try asking:
                  </Typography>
                  {suggestions.map((suggestion, index) => (
                    <Chip
                      key={index}
                      label={suggestion}
                      onClick={() => handleSuggestionClick(suggestion)}
                      sx={{ 
                        justifyContent: 'flex-start',
                        height: 'auto',
                        py: 1,
                        '& .MuiChip-label': {
                          whiteSpace: 'normal',
                          textAlign: 'left'
                        }
                      }}
                    />
                  ))}
                </Stack>
              )}
            </Box>
          ) : (
            <List sx={{ p: 0 }}>
              {messages.map((message, index) => (
                <ListItem
                  key={index}
                  sx={{
                    flexDirection: 'column',
                    alignItems: message.role === 'user' ? 'flex-end' : 'flex-start',
                    px: 0,
                    py: 1
                  }}
                >
                  <Paper
                    sx={{
                      p: 2,
                      maxWidth: '85%',
                      bgcolor: message.role === 'user' ? 'primary.main' : 'background.paper',
                      color: message.role === 'user' ? 'primary.contrastText' : 'text.primary',
                      borderRadius: 2,
                      boxShadow: 1
                    }}
                  >
                    <MarkdownText 
                      text={message.content} 
                      isUserMessage={message.role === 'user'} 
                    />
                  </Paper>
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, px: 1 }}>
                    {new Date(message.created_at).toLocaleTimeString([], { 
                      hour: '2-digit', 
                      minute: '2-digit' 
                    })}
                  </Typography>
                </ListItem>
              ))}
              
              {/* Typing Indicator */}
              {loading && (
                <ListItem sx={{ px: 0, py: 1 }}>
                  <Paper
                    sx={{
                      p: 2,
                      bgcolor: 'background.paper',
                      borderRadius: 2,
                      boxShadow: 1
                    }}
                  >
                    <CircularProgress size={20} />
                  </Paper>
                </ListItem>
              )}
              
              <div ref={messagesEndRef} />
            </List>
          )}
        </Box>

        {/* Input Area */}
        <Box
          component="form"
          onSubmit={handleSubmit}
          sx={{
            p: 2,
            borderTop: 1,
            borderColor: 'divider',
            bgcolor: 'background.paper'
          }}
        >
          <Box sx={{ display: 'flex', gap: 1 }}>
            <TextField
              fullWidth
              multiline
              maxRows={4}
              placeholder="Ask me anything about ClutterLogix..."
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyPress={handleKeyPress}
              disabled={loading}
              size="small"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 3
                }
              }}
            />
            <Button
              type="submit"
              variant="contained"
              disabled={!inputValue.trim() || loading}
              sx={{
                minWidth: 56,
                height: 56,
                borderRadius: 3
              }}
            >
              <SendIcon />
            </Button>
          </Box>
        </Box>
      </Box>
    </Drawer>
  );
};

export default ChatDrawer;
